import Cookies from "../utilities/cookies";
import StatefulSettings, { STATE_KEY_TOUR_STATE } from "./StatefulSettings";
import Database from "../utilities/database";
import {
  IPendingItem,
  ISystemConfig,
  ISettingsItem,
  ControlRecord,
  IEventCustomDecisionResponse,
  IReward,
  CookieShape,
  IShowNotificationState,
  IUserRecord,
  IPermissionItem,
  IEventItem,
  IPenaltyItem,
  ITaskItem,
  ItemAdditionOptions,
  IRouletteRequest,
  TaskBasketItem,
  PenaltyBasketItem,
  AuditMessage,
  IRequestLockState,
  EventItem,
  PermissionItem,
  ITaskVoteConfig,
  ICustomSchedule,
  ITypingStats,
  TourStep,
} from "../ts/Definitions";
import { Cookies as reactCookie } from "react-cookie";
import {
  PendingUpdateType,
  AuditActionTypes,
  RewardType,
  AvailabilityDayType,
  TraceLevel,
} from "../ts/enums";
import ConfigCache, { FEATURE_FLAG_GHOST_USER } from "../utilities/ConfigCache";
import Emitter, { ShowHelp } from "../utilities/Emitter";
import {
  initialiseMessaging,
  MessageType,
  NotificationType,
  sendBankIncreaseMessageToMember,
  sendExceptionMessage,
  sendPerkMessageToMember,
  sendRequestProof,
} from "./messaging";
import {
  getAnalytics,
  isSupported,
} from "firebase/analytics";
import { endOfToday, isBefore, isTomorrow, startOfTomorrow } from "date-fns";
import { formatValidDate } from "../utilities/dateUtils";
import ProfileSettings from "./ProfileSettings";
import Settings from "./settings";
import { getLocation } from "../utilities/externalApiUtils";
import Timer from "../utilities/Timer";
import GuestSettings from "./GuestSettings";
import { AuthenticationService } from "../services/AuthenticationService";
import { SessionManager } from "../services/SessionManager";
import { MessageService } from "../services/MessageService";
import { SchedulerManager } from "../services/SchedulerManager";

declare global {
  interface Window {
    FIREBASE_APPCHECK_DEBUG_TOKEN?: boolean;
  }
}

// TEMPORARY: cold-start load performance logging — remove before next deploy
const loadPerf = (label: string, ...extra: unknown[]) => {
  if (typeof performance === 'undefined') return;
  if (import.meta.env.VITE_ENABLE_LOAD_PERF !== 'true') return;
  const g = globalThis as any;
  if (!g.__loadPerfT0) g.__loadPerfT0 = performance.now();
  console.log(`[LOAD_PERF] +${(performance.now() - g.__loadPerfT0).toFixed(0)}ms | ${label}`, ...extra);
};

export class SessionController {
  private handleMinuteTick = (): void => {
    const userSettings = this.sessionManager.getCurrentUserSettings();
    if (userSettings) userSettings.heartbeat();
  };

  private minuteTimer: Timer = new Timer({
    interval: 60000,
    callbacks: [this.handleMinuteTick],
  });

  private displayMode: string = "browser";
  private displayModeListener?: () => void;

  private authService: AuthenticationService;
  private sessionManager: SessionManager;
  private messageService: MessageService;
  private schedulerManager: SchedulerManager;
  private database!: Database;
  public cookiesService: Cookies;
  public showMessage: (args: IShowNotificationState) => void = () => ({});
  private isLoggedOn = false;
  private configCache!: ConfigCache;
  private guestSettings!: GuestSettings;
  private localMode = false;
  constructor(dbInstance?: Database) {
    this.localMode = import.meta.env.VITE_LOCAL_MODE === 'true' || false;
    this.authService = new AuthenticationService(this.localMode);
    this.sessionManager = new SessionManager();
    this.messageService = new MessageService();
    this.schedulerManager = new SchedulerManager();
    this.cookiesService = this.sessionManager.cookiesService;
    this.initialiseInstance(dbInstance);
  }

  /**
   * Initializes Firebase services (app, auth, database)
   */
  private initializeFirebaseServices(dbInstance?: Database): void {
    this.authService.initializeFirebase(dbInstance);
    this.database = this.authService.getDatabase();
  }

  /**
   * Initializes application-level services (cache, messaging, guest settings)
   */
  private initializeApplicationServices(): void {
    this.configCache = new ConfigCache(this.database);
    this.authService.setConfigCache(this.configCache);
    this.authService.setShowMessage(this.showMessage);
    initialiseMessaging(this.configCache);
    this.guestSettings = new GuestSettings(this.database, this.authService.getAuth());
    this.sessionManager.initialize(
      this.database,
      this.authService.getAuth(),
      this.configCache,
      this.guestSettings,
      this.minuteTimer
    );
    this.messageService.initialize(this.database);
    this.messageService.setShowMessage(this.showMessage);
    this.schedulerManager.initialize(
      this.cookiesService,
      this.minuteTimer,
      this.database,
      this.displayMode
    );
    this.schedulerManager.setShowMessage(this.showMessage);
    if (import.meta.env.PROD) {
      void isSupported()
        .then((ok) => ok && getAnalytics())
        .catch((error) => console.error("Failed to initialize Analytics:", error));
    }
  }

  /**
   * Initializes the SessionController instance
   */
  private initialiseInstance(dbInstance?: Database): void {
    this.initializeFirebaseServices(dbInstance);
    this.initializeApplicationServices();
  }

  public getDatabase = (): Database => {
    return this.database;
  };

  public getLoggedOnUserOwnerProfileSettings = (): ProfileSettings => {
    return this.getLoggedOnUserSettings().getProfileSettingInstance();
  };

  public getLoggedOnUserSettings = (): StatefulSettings => {
    return this.sessionManager.getLoggedOnUserSettings();
  };

  public getConfig = (): ConfigCache => this.configCache;

  private initialised = false;
  public initialise = (
    cookies: reactCookie,
    showMessage: (args: IShowNotificationState) => void
  ): void => {
    if (this.initialised) return;
    this.initialised = true;
    this.showMessage = showMessage;
    this.sessionManager.initialise(cookies, showMessage);
  };

  public addCookieChangedListener = (callback: () => void): void => {
    this.sessionManager.addCookieChangedListener(callback);
  };

  public unsubscribeCookieChangedListener = (callback: () => void): void => {
    this.sessionManager.unsubscribeCookieChangedListener(callback);
  };

  public userLogout() {
    this.sessionManager.userLogout();
    this.schedulerManager.cleanupScheduler();
  }

  private userLoggedOn = (userRecord: ControlRecord, isNew: boolean): void => {
    this.schedulerManager.cleanupScheduler();
    this.sessionManager.userLoggedOn(userRecord, isNew, this.stateAvailableCallback);
    this.isLoggedOn = this.sessionManager.isUserLoggedOn();
    this.schedulerManager.setUserSettings(this.sessionManager.getCurrentUserSettings(), userRecord.isAdmin);
  };

  private controlRecordCallback = (): void => {
    const userSettings = this.sessionManager.getCurrentUserSettings();
    userSettings?.unsubscribeToControlRecord(this.controlRecordCallback); // stop listening to updates once we have what we want
    this.handleHeartbeat();
  };

  private handleHeartbeat = (): void => {
    const userSettings = this.sessionManager.getCurrentUserSettings();
    if (!userSettings) return;
    const { heartbeat } = userSettings.getControl();
    if (heartbeat
      && heartbeat !== 'newUser' &&
      import.meta.env.VITE_VERSION &&
      import.meta.env.VITE_VERSION !== heartbeat
    ) {
      const version = import.meta.env.VITE_VERSION.split(".");
      const heartbeatVersion = userSettings
        ?.getControl()
        .heartbeat.split(".");
      userSettings.updateControlAttributes({ displayMode: this.displayMode });
      const major = parseInt(version[0]);
      const minor = parseInt(version[1]);
      const hMajor = parseInt(heartbeatVersion[0]);
      const hMinor = parseInt(heartbeatVersion[1]);
      if (hMajor !== major || hMinor !== minor)
        Emitter.emit(ShowHelp, "New Features");
      if (hMajor !== major || hMinor !== minor)
        Emitter.emit(ShowHelp, "New Features");
    }
    userSettings?.heartbeat();
  };

  public createScheduler = async (): Promise<void> => {
    loadPerf(`SessionController.createScheduler called`);
    const userSettings = this.sessionManager.getCurrentUserSettings();
    if (!userSettings) return;
    const isAdmin = userSettings.isAdmin();
    await this.schedulerManager.createScheduler(
      isAdmin,
      this.stateAvailableCallback,
      () => this.sessionManager.notifyUserSettingsCallbacks()
    );
  };

  public wakeLiveSession = (): void => {
    const userSettings = this.sessionManager.getCurrentUserSettings();
    if (!userSettings) return;

    Database.performIntegrityCheck = true;
    if (!this.minuteTimer.handle) this.minuteTimer.start();
    this.handleHeartbeat();
    this.sessionManager.notifyUserSettingsCallbacks();
    void this.createScheduler();
  };

  private stateAvailableCallback = (): void => {
    const userSettings = this.sessionManager.getCurrentUserSettings();
    loadPerf(`SessionController.stateAvailableCallback fired`, { isLoaded: userSettings?.isLoaded() ?? false });
    if (!userSettings || !userSettings.isLoaded()) return; // wait till we have both the owned list and the state before we can start to determine what we need to initialise
    userSettings.unsubscribeToViewChanges(this.stateAvailableCallback); // stop listening to updates once we have what we want
    void this.createScheduler();
  };

  public getSettingsAndThen = async (
    userid: string,
    callback: (settings: StatefulSettings) => void
  ): Promise<void> => {
    await this.sessionManager.getSettingsAndThen(userid, callback);
  };

  public getUserSettings = (userid: string): StatefulSettings => {
    return this.sessionManager.getUserSettings(userid);
  };

  // registers the user or logs on if user already exists
  // called from log on screen
  // >> entry point
  public handleLogOn = async (
    username: string,
    email: string,
    password: string,
    registerUser: boolean,
    autoLogon: boolean
  ): Promise<boolean> => {
    const config = await this.configCache.fetchConfig();
    const data = await getLocation();
    if (config && config.bannedIps) {
      const ban = config.bannedIps.find(
        (ban) =>
          data.ipAddress?.includes(ban?.ip) ||
          data.ip?.includes(ban?.ip) ||
          ban?.ip.toLowerCase().trim() === username.toLowerCase().trim()
      );
      if (ban !== undefined) {
        this.showMessage({
          message: `You have been banned from Tarlings. ${ban.reason}`,
          persist: true,
          error: true,
          notificationType: NotificationType.ErrorInfo,
        });
        return false;
      }
    }

    if (registerUser) {
      return this.registerNewUser(username, password, autoLogon);
    }

    const cookie = this.cookiesService.getUserCredentials();
    if (
      this.isLoggedOn &&
      password &&
      username &&
      cookie.username === username?.toLowerCase() &&
      cookie.password !== password
    ) {
      // the user has changed their password, let's update it
      await this.updateLogonPassword(username, password);
      this.showMessage({
        message: "Password Changed",
        persist: true,
        notificationType: NotificationType.General,
      });
      this.sessionManager.notifyUserSettingsCallbacks();
      return this.isLoggedOn;
    }

    const aliasParts = username.split("@as:");
    let alias: string | undefined = undefined;
    if (aliasParts.length > 1) {
      alias = aliasParts[1];
      username = aliasParts[0];
    }

    this.isLoggedOn = await this.logExistingUserOn(
      {
        username,
        tempEmail: email,
        password,
        userid:
          cookie.username?.toLowerCase() === username
            ? cookie.userid?.toLowerCase()
            : "",
        autoLogon,
      },
      alias
    );

    if (!this.isLoggedOn) {
      this.showMessage({
        message: "Unknown User or Incorrect password",
        persist: true,
        notificationType: NotificationType.ErrorInfo,
      });
    } else {
      await this.authService.logAnalyticsEvent(username);
    }
    return this.isLoggedOn;
  };

  // logs the user on ans if not already set up it initialised the user with new settings. if user is not registering and user id not found then false returned, likewise if registering and user already exists then false returned
  // called via log on screen and also via initialisation method
  private logExistingUserOn = async (
    creds: CookieShape,
    alias?: string
  ): Promise<boolean> => {
    this.isLoggedOn = false;
    const result = await this.authService.logExistingUserOn(creds, alias, this.cookiesService);
    if (result.success && result.userRecord) {
      this.isLoggedOn = true;
      this.userLoggedOn(result.userRecord, false);
    }
    return result.success;
  };

  // logs the user on ans if not already set up it initialised the user with new settings. if user is not registering and user id not found then false returned, likewise if registering and user already exists then false returned
  // called via log on screen and also via initialisation method
  private registerNewUser = async (
    username: string,
    password: string,
    autoLogon: boolean
  ): Promise<boolean> => {
    this.isLoggedOn = false;
    const result = await this.authService.registerNewUser(username, password, autoLogon, this.cookiesService);
    if (result.success && result.userRecord) {
      this.isLoggedOn = true;
      this.userLoggedOn(result.userRecord, true);
    }
    return result.success;
  };

  public resetPassword = (user: IUserRecord): void => {
    this.authService.resetPassword(
      user,
      () => {
        this.showMessage({
          message: `Password Successfully Changed to ${import.meta.env.VITE_RESET_PASSWORD}`,
          notificationType: NotificationType.General,
        });
      },
      (userMail) => {
        this.showMessage({
          message: `Unable to reallocate user - delete ${userMail} from database Auth and try again`,
          notificationType: NotificationType.ErrorInfo,
        });
        window.open(
          "https://console.firebase.google.com/u/1/project/tarlingsstore/authentication/users",
          "_blank"
        );
      }
    );
  };

  public getAutoLogon = (): boolean => this.cookiesService.autoLogon;

  public updateAutoLogon = (): boolean => {
    this.cookiesService.setUser(
      this.cookiesService.password,
      this.cookiesService.username,
      this.cookiesService.userid,
      true,
      !this.cookiesService.autoLogon
    );
    this.showMessage({
      message: "Auto Logon Updated",
      notificationType: NotificationType.General,
    });
    return true;
  };

  public updateLogonPassword = async (
    username: string,
    password: string
  ): Promise<boolean> => {
    await this.authService.updatePassword(password);
    this.cookiesService.setUser(
      password,
      username,
      this.cookiesService.userid,
      true,
      this.cookiesService.autoLogon
    );
    this.showMessage({
      message: "Password Successfully Changed",
      notificationType: NotificationType.General,
    });
    return true;
  };

  public updateEmailAddress = async (newUserName: string): Promise<boolean> => {
    if (!newUserName?.trim()) return false;
    await this.authService.updateEmailAddress(newUserName);
    this.cookiesService.setState("username", newUserName);
    const userSettings = this.sessionManager.getCurrentUserSettings();
    userSettings?.updateUserName(newUserName);
    this.showMessage({
      message: "Username Successfully Changed",
      notificationType: NotificationType.General,
    });
    return true;
  };

  public subscribeToLogOn = (
    callback: (success: boolean, isNew: boolean) => void
  ): void => {
    this.sessionManager.subscribeToLogOn(callback);
  };

  public unsubscribeToLogOn = (
    callback: (success: boolean, isNew: boolean) => void
  ): void => {
    this.sessionManager.unsubscribeToLogOn(callback);
  };

  public subscribeToUserSettings = (
    callback: (userSettings?: StatefulSettings) => void
  ): void => {
    this.sessionManager.subscribeToUserSettings(callback);
  };

  public unsubscribeToUserSettings = (
    callback: (userSettings?: StatefulSettings) => void
  ): void => {
    this.sessionManager.unsubscribeToUserSettings(callback);
  };

  public setAdminMode = async (isAdmin: boolean): Promise<void> => {
    await this.schedulerManager.setAdminMode(isAdmin);
  };

  /**
   * reruns the initialisation of the new day on the user session when they pick up the request for refresh
   * does not perform any validation on the current day or tasks and clears all tasks in progress
   * including those related to votes, master reset when we have issues
   * @param userSettings the user to reset
   * @returns if queuing the update was a success
   */
  public sendRefreshRequest = (userSettings: StatefulSettings): boolean => {
    return this.messageService.sendRefreshRequest(userSettings);
  };

  /**
   * pushes the items into the pending state ready for the user to pick up to process after first validating it is not already
   * in progress in the session. Supports adding to pillory and adding to session.
   * This is fired from clicking add on the settingItem
   * @param proposedItems items to add to the user session, for each we check if valid and not already in the session
   * @param userSettings the user session to update
   * @returns if queuing the update was a success
   */
  public sendAddRequest = (
    proposedItems: IPendingItem[],
    userSettings: StatefulSettings
  ): boolean => {
    return this.messageService.sendAddRequest(proposedItems, userSettings);
  };

  /**
   * Allows owners to remove items from a session by sending a state to the user session
   * to remove the item and then tidy up and dependencies on that session
   * This is fired from clicking REMOVE on the basket item
   * @param item the item to remove
   * @param userSettings the user to remove the item for
   * @returns if queuing the update was a success
   */
  public sendRemoveRequest = (
    item: ISettingsItem,
    userSettings: StatefulSettings
  ): boolean => {
    return this.messageService.sendRemoveRequest(item, userSettings);
  };

  public sendScheduleChange = (
    item: ICustomSchedule,
    date: string,
    userSettings: StatefulSettings
  ): boolean => {
    return this.messageService.sendScheduleChange(item, date, userSettings);
  };

  /**
   * immediately releases an event which is pending and awaiting a trigger time. allows events to be ran and
   * tasks that rely on the event to be fired or ended - useful bypass when users are unable to wait or need to
   * finish their tasks sooner than event would allow.
   * this is fired from clicking REMOVE on the basketitem
   * @param item the event to release
   * @param userSettings the sessopm to update
   * @returns if queuing the update was a success
   */
  public sendEventRelease = (
    item: ISettingsItem,
    userSettings: StatefulSettings
  ): boolean => {
    return this.messageService.sendEventRelease(item, userSettings);
  };

  /**
   * fired when a permission is already decided and the owner wishes to change the decision and chance the result
   * allows different count and even result to be updated on the schedule for the day
   * fired from the change button on the settingitem
   * @param item the item to change with new settings
   * @param userSettings the session to update
   * @returns if queuing the update was a success
   */
  public sendPermissionChange = (
    item: IEventCustomDecisionResponse,
    userSettings: StatefulSettings
  ): boolean => {
    return this.messageService.sendPermissionChange(item, userSettings);
  };

  public sendTaskChange = (
    item: TaskBasketItem | PenaltyBasketItem,
    userSettings: StatefulSettings
  ): boolean => {
    return this.messageService.sendTaskChange(item, userSettings);
  };

  /**
   * Sends a batch of schedule edit operations (removes, reschedules, adds) as pending items.
   */
  public sendScheduleEdits = (
    operations: IPendingItem[],
    userSettings: StatefulSettings
  ): boolean => {
    return this.messageService.sendScheduleEdits(operations, userSettings);
  };

  /**
   * fired when a permission is already decided and the owner wishes to change the decision and chance the result
   * allows different count and even result to be updated on the schedule for the day
   * fired from the change button on the settingitem
   * @param item the item to change with new settings
   * @param userSettings the session to update
   * @returns if queuing the update was a success
   */
  public sendRequestProof = (
    item: TaskBasketItem | PenaltyBasketItem,
    userSettings: StatefulSettings
  ): boolean => {
    return this.messageService.sendRequestProof(
      item,
      userSettings,
      this.getLoggedOnUser()
    );
  };

  public updateConfig = (config: ISystemConfig): void => {
    void this.database.saveConfig(config).catch((error) =>
      console.error("Failed to save config:", error)
    );
  };

  public getSystemUserId = async (
    callback?: (user: ControlRecord | undefined) => void
  ): Promise<ControlRecord | undefined> => {
    const systemUser = new ControlRecord(
      await this.database.lookupSystemUser()
    );
    if (callback) callback(systemUser);
    return systemUser;
  };

  public allowedFeature = (
    flag: string,
    defaultValue: boolean,
    user?: string
  ): boolean => {
    if (!user)
      return this.configCache.allowedFeature(
        flag,
        this.sessionManager.getCurrentUserSettings()?.getControl()?.username,
        defaultValue
      );
    return this.configCache.allowedFeature(flag, user, defaultValue);
  };

  public incrementHelpTopicCount = (title: string): void => {
    this.configCache.incrementHelpTopicCount(title);
  };

  public reward = (
    perk: IReward,
    reason: string | undefined,
    toUser: Settings
  ): void => {
    const userSettings = this.sessionManager.getCurrentUserSettings();
    if (!userSettings) return;
    void this.database.auditUser(
      [
        new AuditMessage(
          AuditActionTypes.Session,
          "Reward Sent",
          `${perk.name} rewarded to ${toUser.getControl().username}`,
          TraceLevel.info,
          true,
          perk.cost
        ),
      ],
      userSettings.getControl()
    ).catch((error) => console.error("Failed to audit reward:", error));

    if (perk.type === RewardType.transfer) {
      toUser.payOwner(perk.cost * -1);
      void toUser.auditBankChange(reason ?? "Reward", perk.cost);
      sendBankIncreaseMessageToMember(
        perk,
        reason,
        userSettings,
        toUser.getControl()
      );
    } else {
      toUser.payOwner(perk.cost * -1);
      toUser.auditUserAction(
        `${perk.name} - ${reason ?? "Reward"}`,
        AuditActionTypes.Session
      );
      const currentUserSettings = this.sessionManager.getCurrentUserSettings();
      if (currentUserSettings) {
        sendPerkMessageToMember(
          perk,
          reason,
          currentUserSettings,
          toUser.getControl()
        );
      }
    }
    this.showMessage({
      message: `${perk.name} rewarded to ${toUser.getControl().username}`,
      notificationType: NotificationType.General,
    });
  };

  public addPermissionToSchedule = (
    checkoutPerm: IPermissionItem,
    scheduleReason: string
  ) => {
    this.schedulerManager.addPermissionToSchedule(checkoutPerm, scheduleReason);
  };

  public handleItemFailed = (item: ISettingsItem) => {
    this.schedulerManager.handleItemFailed(item);
  };

  public handlePlayItem = (item: ISettingsItem) => {
    this.schedulerManager.handlePlayItem(item);
  };

  public handleRouletteTask = (item: ITaskItem | IPenaltyItem, options?: string[]): void => {
    this.schedulerManager.handleRouletteTask(item, options);
  };

  public auditUser = (message: AuditMessage): void => {
    void this.database.auditUser([message], this.getLoggedOnUser()).catch((error) =>
      console.error("Failed to audit user:", error)
    );
  };

  public onAddToSchedule = (item: ISettingsItem, scheduleReason: string) => {
    this.schedulerManager.onAddToSchedule(item, scheduleReason);
  };

  public addTaskToSchedule = (
    inputItem: ITaskItem,
    sourceItem: EventItem | PermissionItem | undefined,
    scheduleReason: string,
    inputOptions?: ItemAdditionOptions
  ) => {
    return this.schedulerManager.addTaskToSchedule(inputItem, sourceItem, scheduleReason, inputOptions);
  };

  public addPenaltyToSchedule = (
    inputItem: IPenaltyItem,
    sourceItem: EventItem | PermissionItem | undefined,
    scheduleReason: string,
    inputOptions?: ItemAdditionOptions
  ) => {
    return this.schedulerManager.addPenaltyToSchedule(inputItem, sourceItem, scheduleReason, inputOptions);
  };

  public refreshStartOfDaySchedule = (eventItem: IEventItem) => {
    this.schedulerManager.refreshStartOfDaySchedule(eventItem);
  };

  public addSystemGeneratedPenaltyItem = (
    item: IPenaltyItem,
    reason: string
  ) => {
    this.schedulerManager.addSystemGeneratedPenaltyItem(item, reason);
  };

  public setStateToCurrentDay = () => {
    return this.schedulerManager.setStateToCurrentDay();
  };

  public sendLines(
    lines: string,
    line: string,
    expectedCount: number,
    mistakes: number,
    itemKey: string,
    allowRedoLines: boolean,
    stats: ITypingStats
  ) {
    return this.schedulerManager.sendLines(lines, line, expectedCount, mistakes, itemKey, allowRedoLines, stats);
  }

  public setRouletteResult(
    item: string | ITaskItem | IPenaltyItem | ITaskVoteConfig,
    rouletteRequest: IRouletteRequest,
    options?: ItemAdditionOptions
  ) {
    return this.schedulerManager.setRouletteResult(item, rouletteRequest, options);
  }

  public saveMorningReport(reviewText: string) {
    return this.schedulerManager.saveMorningReport(reviewText);
  }

  public saveDailyReport(reviewText: string) {
    return this.schedulerManager.saveDailyReport(reviewText);
  }

  public updateControlAttributes(row: Record<string, any>) {
    return this.getLoggedOnUserSettings().updateControlAttributes(row);
  }

  public setRequestLockComplete(requestLockState: IRequestLockState) {
    return this.schedulerManager.setRequestLockComplete(requestLockState);
  }

  public getLoggedOnUser = () => this.sessionManager.getLoggedOnUser();

  public handleException = (
    error: any,
    location?: string,
    componentError?: any
  ) => {
    sendExceptionMessage(this.getLoggedOnUserSettings(), error, location);
    void this.getLoggedOnUserSettings().auditUser(
      new AuditMessage(
        AuditActionTypes.Exception,
        "Error",
        JSON.stringify(error).concat("\r\n", JSON.stringify(componentError)),
        TraceLevel.error
      )
    );
  };

  public approveRequest = (
    {
      userid,
      item,
    }: {
      userid: string;
      item: ISettingsItem;
    },
    messageType: MessageType | undefined
  ) => {
    this.messageService.approveRequest(
      userid,
      item,
      messageType,
      (uid, callback) => void this.getSettingsAndThen(uid, callback),
      (itm, settings) => this.sendRemoveRequest(itm, settings)
    );
  };

  public postponeItem = ({ userid, key }: { userid: string; key: string }) => {
    this.messageService.postponeItem(
      userid,
      key,
      (uid, callback) => void this.getSettingsAndThen(uid, callback),
      (item, settings) => this.sendRemoveRequest(item, settings),
      (items, settings) => this.sendAddRequest(items, settings)
    );
  };

  public getUserControl = (userid: string): ControlRecord | undefined => {
    return this.sessionManager.getUserControl(userid);
  };

  public addTourStep = (
    screen: string,
    steps: TourStep[],
    stepToAdd: TourStep
  ) => {
    this.sessionManager.addTourStep(screen, steps, stepToAdd);
  };

  public updateTourState = (screen: string, step: TourStep) => {
    this.sessionManager.updateTourState(screen, step);
  };
}
